import { RestApiClient } from 'ifield-core-components'
import _ from 'lodash'

/**
 * ユーザー属性取得
 * @param {Number} id ID
 * @returns {Array.<Object>|Object} データ
 */
const getUserAttributes = async (id) => {
  const endPoint = id == null ? 'user_attribute' : `user_attribute/${id}`
  const url = RestApiClient.createApiUrl(endPoint)
  const json = await RestApiClient.executeGetApi(url)
  return json.data
}

/**
 * 初期スロットル間隔(ms)
 */
const INITIAL_THROTTLE_TIME = 30000

/**
 * 初期 state 取得
 * @returns
 */
const getDefaultState = () => ({
  userAttributes: [],
  throttleTime: INITIAL_THROTTLE_TIME,
  throttler: null,
})

export default {
  namespaced: true,
  state: getDefaultState(),
  persistentStates: ['throttleTime'],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      Object.assign(state, getDefaultState())
    },
    /**
     * ユーザー属性を設定する
     * @param {*} state
     * @param {Array.<Object>} attributes ユーザー属性
     */
    setUserAttributes(state, attributes) {
      state.userAttributes = attributes
    },
    /**
     * 特定のユーザー属性を設定する
     * @param {*} state
     * @param {Object} attribute ユーザー属性
     */
    setSpecificUserAttribute(state, attribute) {
      const targetIndex = state.userAttributes.findIndex(
        (l) => l.id === attribute.id
      )
      if (targetIndex >= 0) {
        state.userAttributes[targetIndex] = attribute
      } else {
        state.userAttributes.push(attribute)
      }
    },
    /**
     * スロットル間隔を保存する
     * @param {*} state
     * @param {Number} time スロットル間隔(ms)
     */
    setThrottleTime(state, time) {
      state.throttleTime = +time
    },
    /**
     * スロットル関数を保存する
     * @param {*} state
     * @param {Function} throttler スロットル関数
     */
    setThrottler(state, throttler) {
      state.throttler = throttler
    },
  },
  getters: {
    /**
     * ユーザー属性
     * @param {*} state
     * @returns {Array.<Object>}
     */
    userAttributes: (state) => state.userAttributes,
    /**
     * ユーザー属性(Map)
     * @param {*} state
     * @returns {Map}
     */
    userAttributeMap: (state) =>
      new Map(
        state.userAttributes.map((attribute) => [attribute.id, attribute])
      ),
    /**
     * 特定IDのユーザー属性を取得
     * @param {*} state
     * @param {*} getters
     * @returns {Function} IDを引数に特定IDのユーザー属性を返す関数
     */
    getUserAttributeById: (state, getters) => (id) =>
      getters.userAttributeMap.get(id),
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * ユーザー属性を更新する
     */
    async updateUserAttributes({ commit }) {
      const attributes = await getUserAttributes()
      commit('setUserAttributes', attributes)
    },
    /**
     * 特定のユーザー属性を更新する
     * @param {Number} id 対象のユーザー属性ID
     */
    async updateSpecificUserAttribute({ commit }, id) {
      const attribute = await getUserAttributes(id)
      commit('setSpecificUserAttribute', attribute)
      return attribute
    },
    /**
     * ユーザー属性を更新する(スロットル)
     */
    async updateUserAttributesWithThrottle({ state, commit, dispatch }) {
      if (!state.throttler) {
        const throttler = _.throttle(
          () => dispatch('updateUserAttributes'),
          state.throttleTime,
          { trailing: false }
        )
        commit('setThrottler', throttler)
      }
      return state.throttler()
    },
    /**
     * スロットル間隔更新
     * @param {Number} time スロットル間隔(ms)
     */
    updateThrottleTime({ commit }, time) {
      commit('setThrottleTime', +time)
      commit('setThrottler', null)
    },
  },
}
